import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import useAdminCheck from '../../hooks/useAdminCheck';

const AddOnCatalog = () => {
  const [addOnServices, setAddOnServices] = useState([]);
  const [customFeatures, setCustomFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cartLoading, setCartLoading] = useState({});
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { formatCurrency } = useCurrency();
  const isAdmin = useAdminCheck();

  const categories = [
    { value: 'all', label: 'All Add-ons' },
    { value: 'system', label: 'System Add-ons' },
    { value: 'custom', label: 'Custom Add-ons' }
  ];

  const pricingTypeLabels = {
    'one_time': 'One-time',
    'recurring': 'Monthly',
    'usage_based': 'Per Use',
    'tiered': 'Volume-based'
  };

  useEffect(() => {
    fetchAddOns();
  }, [selectedCategory]);

  const fetchAddOns = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      // Fetch system add-on services (filter for 4 core types)
      const addOnsResponse = await fetch(`${backendUrl}/api/add-on-services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let systemAddons = [];
      if (addOnsResponse.ok) {
        const allAddons = await addOnsResponse.json();
        // Filter to only show 4 core capacity add-ons
        systemAddons = allAddons.filter(addon => {
          if (!addon.limit_increase) return false;
          const limitKeys = Object.keys(addon.limit_increase);
          return limitKeys.some(key => 
            ['business_plans', 'users', 'scenarios', 'subsidiaries'].includes(key)
          );
        });
      }
      
      // Fetch custom features (custom add-ons) - public endpoint
      let customAddons = [];
      try {
        const customResponse = await fetch(`${backendUrl}/api/custom-add-ons`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (customResponse.ok) {
          customAddons = await customResponse.json();
          console.log('[ADD-ONS] Custom add-ons fetched:', customAddons.length);
          // Mark them as custom type
          customAddons = customAddons.map(c => ({ ...c, type: 'CUSTOM', category: 'custom' }));
        } else {
          const errorText = await customResponse.text();
          console.error('[ADD-ONS] Failed to fetch custom add-ons:', customResponse.status, errorText);
        }
      } catch (customError) {
        console.error('[ADD-ONS] Exception fetching custom add-ons:', customError);
      }
      
      // Mark system addons
      systemAddons = systemAddons.map(s => ({ ...s, type: 'SYSTEM' }));
      
      console.log('[ADD-ONS] Setting state - System:', systemAddons.length, 'Custom:', customAddons.length);
      console.log('[ADD-ONS] System addons sample:', systemAddons[0]?.display_name || 'none');
      console.log('[ADD-ONS] Custom addons sample:', customAddons[0]?.display_name || 'none');
      setAddOnServices(systemAddons);
      setCustomFeatures(customAddons);
      
    } catch (error) {
      console.error('[ADD-ONS] Error fetching add-ons:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (serviceId, quantity = 1, billingCycle = null) => {
    setCartLoading(prev => ({ ...prev, [serviceId]: true }));
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/cart/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          item_type: 'add_on',
          item_id: serviceId,
          quantity,
          billing_cycle: billingCycle,
          configuration: {}
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Show success message or notification
        alert('Add-on added to cart successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to add to cart: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding to cart. Please try again.');
    } finally {
      setCartLoading(prev => ({ ...prev, [serviceId]: false }));
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'capacity': '👥',
      'features': '⚡',
      'support': '🎯',
      'storage': '💾',
      'analytics': '📊'
    };
    return icons[category] || '🔧';
  };

  const formatPrice = (service) => {
    const formattedAmount = formatCurrency(service.base_price, 'USD');
    
    if (service.pricing_type === 'one_time') {
      return formattedAmount;
    } else if (service.pricing_type === 'recurring') {
      const cycle = service.billing_cycle === 'monthly' ? '/mo' : '/yr';
      return `${formattedAmount}${cycle}`;
    } else if (service.pricing_type === 'usage_based') {
      return `${formattedAmount} per use`;
    } else {
      return `From ${formattedAmount}`;
    }
  };

  const isAvailableForTier = (service) => {
    // Admins have access to everything
    if (isAdmin) {
      return true;
    }
    
    // If no subscription, allow add-ons that are available to all tiers or include starter
    if (!subscription || !subscription.tier) {
      if (service.available_tiers.length === 0) return true;
      return service.available_tiers.includes('starter') || service.available_tiers.includes('professional');
    }
    
    if (service.available_tiers.length === 0) return true;
    return service.available_tiers.includes(subscription.tier.name);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Add-on Services</h1>
        <p className="text-gray-600">Enhance your plan with additional features and capabilities</p>
      </div>

      {/* Category Filter */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <Button
              key={category.value}
              variant={selectedCategory === category.value ? "default" : "outline"}
              onClick={() => setSelectedCategory(category.value)}
              className="h-9"
            >
              {category.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Current Plan Info */}
      {subscription && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">Current Plan: {subscription.tier?.display_name}</h3>
              <p className="text-sm text-blue-700">
                Users: {subscription.tier?.limits?.users || 'Unlimited'} | 
                Business Plans: {subscription.tier?.limits?.business_plans === -1 ? 'Unlimited' : subscription.tier?.limits?.business_plans}
              </p>
            </div>
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              {subscription.billing_cycle === 'monthly' ? 'Monthly' : 'Annual'}
            </Badge>
          </div>
        </div>
      )}

      {/* Add-on Services Grid */}
      <div className="space-y-8">
        {/* System Add-ons Section */}
        {(selectedCategory === 'all' || selectedCategory === 'system') && addOnServices.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-green-600">🔢</span>
              System Add-ons - Core Capacity
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Increase your limits for business plans, team members, scenarios, and subsidiaries
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {addOnServices.map((service) => {
                const isAvailable = isAvailableForTier(service);
                const isLoading = cartLoading[service.id];

                return (
                  <Card key={service.id} className={`relative ${!isAvailable ? 'opacity-60' : ''} border-green-200`}>
                    <CardHeader className="pb-3">
                      <div>
                        <CardTitle className="text-lg">{service.display_name}</CardTitle>
                        <Badge variant="outline" className="text-xs mt-2 bg-green-50 text-green-700 border-green-300">
                          SYSTEM
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <CardDescription className="text-sm">
                        {service.description}
                      </CardDescription>

                      {/* Capacity Increase */}
                      {service.limit_increase && (
                        <div className="p-2 bg-green-50 rounded border border-green-200">
                          <div className="text-xs text-gray-600 mb-1">Increases:</div>
                          {Object.entries(service.limit_increase).map(([key, value]) => (
                            <div key={key} className="text-sm font-semibold text-green-700">
                              {key.replace(/_/g, ' ')}: +{value}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Price */}
                      <div className="pt-2 border-t">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatPrice(service)}
                        </div>
                        {service.price_per_unit > 0 && (
                          <div className="text-xs text-gray-500">
                            {formatCurrency(service.price_per_unit, 'USD')} per unit
                          </div>
                        )}
                      </div>

                      {/* Add to Cart Button */}
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700"
                        disabled={!isAvailable || isLoading}
                        onClick={() => addToCart(service.id)}
                      >
                        {isLoading ? 'Adding...' : isAvailable ? 'Add to Cart' : 'Not Available'}
                      </Button>

                      {!isAvailable && (
                        <p className="text-xs text-gray-500 text-center">
                          Available for: {service.available_tiers.join(', ')}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Custom Add-ons Section */}
        {(selectedCategory === 'all' || selectedCategory === 'custom') && customFeatures.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-purple-600">⚙️</span>
              Custom Add-ons - Premium Services
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Premium support, training packages, and specialized services
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {customFeatures.map((service) => {
                const isAvailable = isAvailableForTier(service);
                const isLoading = cartLoading[service.id];
                
                // Debug logging
                console.log('[CUSTOM RENDER]', service.display_name, 'Type:', service.type, 'Has limit_increase:', !!service.limit_increase);

                return (
                  <Card key={service.id} className={`relative ${!isAvailable ? 'opacity-60' : ''} border-purple-200`}>
                    <CardHeader className="pb-3">
                      <div>
                        <CardTitle className="text-lg">{service.display_name}</CardTitle>
                        <Badge variant="outline" className="text-xs mt-2 bg-purple-50 text-purple-700 border-purple-300">
                          CUSTOM
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <CardDescription className="text-sm">
                        {service.description}
                      </CardDescription>

                      {/* Price */}
                      <div className="pt-2 border-t">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatPrice(service)}
                        </div>
                        {service.price_per_unit > 0 && (
                          <div className="text-xs text-gray-500">
                            {formatCurrency(service.price_per_unit, 'USD')} per unit
                          </div>
                        )}
                        {service.minimum_quantity > 1 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Min: {service.minimum_quantity} units
                          </p>
                        )}
                      </div>

                      {/* Add to Cart Button */}
                      <Button 
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        disabled={!isAvailable || isLoading}
                        onClick={() => addToCart(service.id, service.minimum_quantity, service.billing_cycle)}
                      >
                        {isLoading ? 'Adding...' : isAvailable ? 'Add to Cart' : 'Not Available'}
                      </Button>

                      {!isAvailable && (
                        <p className="text-xs text-gray-500 text-center">
                          Available for: {service.available_tiers?.join(', ') || 'All tiers'}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {addOnServices.length === 0 && customFeatures.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🔧</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No add-ons found</h3>
            <p className="text-gray-600">
              {selectedCategory === 'all' 
                ? 'No add-on services are currently available.'
                : `No add-ons found in the ${categories.find(c => c.value === selectedCategory)?.label} category.`
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddOnCatalog;